import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { scrubSignedUrls } from '../util/sentry-scrubbing';

/**
 * Reports failures in flows that block a user from getting their work done —
 * uploading a document, joining an office, adding or editing a licence — so
 * they surface in Sentry as actionable issues instead of a support email that
 * says "it didn't work".
 *
 * Call sites should use this rather than `Sentry.*` directly: it keeps the tag
 * and extra shape consistent, fingerprints per operation, and rate limits
 * itself so a bulk import can't emit one issue per row.
 *
 * Operation names follow `<feature>.<action>`, lowercase, kebab-case within a
 * segment: `document.upload`, `licence.add`, `group-invite.respond`. The
 * leading segment becomes the `feature` tag, so keep it stable.
 *
 * ---------------------------------------------------------------------------
 * NEVER put the following in `context` — it is sent to a third party:
 *
 *   File/Blob contents, object URLs   ->  fileExtension, fileSizeBytes, count
 *   file.name / relativePath          ->  (user-authored; often a person's name)
 *   registration payloads             ->  it contains the user's password
 *   bulk-import row values            ->  rowIndex, reason, errorCode
 *   an invitee's email or licence no.  ->  inviteType, hasExistingUser
 *   licence numbers, holder names     ->  state, licenceType, mode
 *   document names, notes, courses    ->  documentType, ownerType
 *   signed file/photo URLs            ->  storage path, variant, fileExtension
 *   a Firebase error's `details`      ->  errorCode
 *
 * Identity is already attached to every event globally by
 * `Sentry.setUser` in authenticated-data.service.ts and current-user.service.ts,
 * so there is no need to re-send the user's id or email here.
 * ---------------------------------------------------------------------------
 */
@Injectable({ providedIn: 'root' })
export class ErrorReportingService {
  /**
   * Enough events to see a pattern, few enough that one broken loop can't
   * bury everything else in the session.
   */
  private readonly maxEventsPerOperation = 5;
  private readonly maxEventsPerSession = 25;

  private readonly countsByOperation = new Map<string, number>();
  private totalEvents = 0;

  private readonly router = inject(Router);

  /** Reports a thrown or rejected error. */
  captureError(operation: string, error: unknown, context?: Record<string, unknown>): void {
    console.error(`[${operation}]`, error, context ?? '');
    this.send(operation, 'error', error, undefined, context);
  }

  /**
   * Reports an operation that completed without throwing but did not succeed.
   * Several of our callables resolve `false` instead of rejecting, so there is
   * no exception to capture.
   */
  captureFailure(operation: string, message: string, context?: Record<string, unknown>): void {
    console.error(`[${operation}] ${message}`, context ?? '');
    this.send(operation, 'error', undefined, message, context);
  }

  /** Reports a failure the user can work around (a skipped row, a fallback that was taken). */
  captureWarning(operation: string, message: string, context?: Record<string, unknown>): void {
    console.warn(`[${operation}] ${message}`, context ?? '');
    this.send(operation, 'warning', undefined, message, context);
  }

  private send(
    operation: string,
    level: 'error' | 'warning',
    error: unknown,
    message: string | undefined,
    context: Record<string, unknown> | undefined,
  ): void {
    // Reporting a failure must never turn into a second failure. Everything
    // below is best-effort; the console.error above is the guaranteed part.
    try {
      const budget = this.claimBudget(operation);

      if (!budget.allowed) {
        return;
      }

      const built = buildCaptureContext(operation, error, context, this.router.url);
      const scope = {
        level,
        tags: built.tags,
        fingerprint: built.fingerprint,
        extra: budget.isLastAllowed
          ? { ...built.extra, suppressingFurtherEvents: true }
          : built.extra,
      };

      if (message !== undefined) {
        this.sendMessage(message, scope);
        return;
      }

      if (error instanceof Error) {
        this.sendException(error, scope);
        return;
      }

      // A non-Error rejection has no stack worth keeping, so it reads better as
      // a message than as Sentry's synthetic "Non-Error exception captured".
      this.sendMessage(`${operation} failed: ${built.extra['errorMessage']}`, scope);
    } catch {
      // Ignore — see above.
    }
  }

  private claimBudget(operation: string): { allowed: boolean; isLastAllowed: boolean } {
    if (this.totalEvents >= this.maxEventsPerSession) {
      return { allowed: false, isLastAllowed: false };
    }

    const used = this.countsByOperation.get(operation) ?? 0;

    if (used >= this.maxEventsPerOperation) {
      return { allowed: false, isLastAllowed: false };
    }

    this.countsByOperation.set(operation, used + 1);
    this.totalEvents += 1;

    return { allowed: true, isLastAllowed: used + 1 === this.maxEventsPerOperation };
  }

  // Seams so specs can assert what would be sent without stubbing the frozen
  // Sentry module namespace.
  protected sendException(error: Error, scope: Record<string, unknown>): void {
    Sentry.captureException(error, scope as Parameters<typeof Sentry.captureException>[1]);
  }

  protected sendMessage(message: string, scope: Record<string, unknown>): void {
    Sentry.captureMessage(message, scope as Parameters<typeof Sentry.captureMessage>[1]);
  }
}

/**
 * Reads the error code off a Firebase error. Callables use bare codes
 * (`permission-denied`), Storage prefixes them (`storage/unauthorized`), and
 * Angular's RuntimeError uses a number (4002). We duck-type rather than
 * `instanceof FirebaseError` because the modular SDK can end up with more than
 * one copy of the class in a bundle.
 */
export function getErrorCode(error: unknown): string | undefined {
  const code = (error as { code?: unknown } | null | undefined)?.code;

  if (typeof code === 'string' && code) {
    return code.slice(0, 100);
  }

  if (typeof code === 'number') {
    return String(code);
  }

  return undefined;
}

/**
 * Builds the Sentry scope for an event. Pure, so the interesting parts are
 * testable without a Sentry client.
 */
export function buildCaptureContext(
  operation: string,
  error: unknown,
  context: Record<string, unknown> | undefined,
  currentUrl: string,
): { tags: Record<string, string>; extra: Record<string, unknown>; fingerprint: string[] } {
  const errorCode = getErrorCode(error);
  const errorName = error instanceof Error ? error.name : error === undefined ? undefined : typeof error;

  const tags: Record<string, string> = {
    operation,
    feature: operation.split('.')[0],
  };

  if (errorCode) {
    tags['error_code'] = errorCode;
  }

  const extra: Record<string, unknown> = {
    currentUrl,
    ...context,
  };

  if (error !== undefined) {
    extra['errorName'] = errorName;
    extra['errorMessage'] = error instanceof Error ? error.message : String(error);
  }

  if (errorCode) {
    extra['errorCode'] = errorCode;
  }

  // Without this, every failed callable groups under one issue: they share a
  // stack inside the Firebase SDK, so Sentry sees them as the same problem.
  return {
    tags,
    extra: scrubSignedUrls(extra),
    fingerprint: ['tracit', operation, errorCode ?? errorName ?? 'unknown'],
  };
}
